import { Controller } from "@hotwired/stimulus"

// "How this was calculated" disclosure panel (ADR-0107 follow-up, card #2527)
// -- reveals a bound element's canonical reporting_metric (label/unit/scope/
// body) on click of the information-circle icon DecksHelper#deck_metric_
// disclosure_icon renders. Attached directly on that icon, one controller
// instance per disclosed element.
//
// PORTAL, not a pre-rendered sibling -- reuses deck_override_reason_hovercard_
// controller.js's documented technique verbatim: a canvas element always sits
// inside .airdrop-slide-scale > .airdrop-slide, which carries its OWN
// permanent CSS `transform: scale(...)` (fit-to-viewport sizing) -- and a
// `transform` on any ancestor makes THAT ancestor the containing block for a
// `position: fixed` descendant, not the real viewport (CSS spec). A panel
// rendered as a child of this icon would size/position itself against the
// SLIDE's box, not the screen. Appending straight onto document.body on open
// sidesteps this entirely -- see that controller's header comment for the
// full writeup; this is the same fix, applied to a click toggle instead of a
// hover.
//
// CLICK, not hover (unlike the reason hovercard): client reports are viewed
// on touch devices as much as desktop, where hover doesn't exist. Opens on
// click of the icon; closes on a second click of the icon, a click outside
// the panel, or Escape.
//
// TEXT NODES ONLY (never innerHTML), mirroring the hovercard's own XSS-safety
// choice: label/unit/scope/body are Ruby-authored strings (ReportingMetric
// fields), not user HTML, but there's no reason to open an escaping surface
// that doesn't need to exist.
export default class extends Controller {
  static values = { label: String, unit: String, scope: String, body: String }

  connect() {
    this._panel = null
    this._outsideClickHandler = (event) => this._handleOutsideClick(event)
    this._keydownHandler = (event) => this._handleKeydown(event)
  }

  disconnect() {
    this._removePanel()
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()

    if (this._panel) {
      this._removePanel()
    } else {
      this._showPanel()
    }
  }

  _showPanel() {
    if (this._panel) return

    const panel = document.createElement("div")
    // The "deck-metric-disclosure-panel" marker class is a stable test hook
    // (no other on-page element carries it) -- everything else is
    // presentation only.
    panel.className =
      "deck-metric-disclosure-panel fixed z-50 max-w-xs rounded-lg border border-blue-200 bg-white px-3 py-2 text-xs text-gray-700 shadow-lg"

    panel.appendChild(this._row(this.labelValue, "font-semibold text-gray-900"))

    if (this.unitValue) {
      panel.appendChild(this._row(`Unit: ${this.unitValue}`, "mt-1 text-gray-500"))
    }
    if (this.scopeValue) {
      panel.appendChild(this._row(`Scope: ${this.scopeValue}`, "mt-1 text-gray-500"))
    }
    if (this.bodyValue) {
      panel.appendChild(this._row(this.bodyValue, "mt-2 whitespace-pre-line text-gray-700"))
    }

    document.body.appendChild(panel)
    this._panel = panel
    this._position()

    // Deferred so the click that opened the panel doesn't immediately close
    // it via the same event bubbling to document.
    setTimeout(() => {
      document.addEventListener("click", this._outsideClickHandler)
      document.addEventListener("keydown", this._keydownHandler)
    }, 0)
  }

  _row(text, className) {
    const row = document.createElement("div")
    row.className = className
    row.textContent = text
    return row
  }

  _position() {
    if (!this._panel) return

    const triggerRect = this.element.getBoundingClientRect()
    const panelHeight = this._panel.offsetHeight
    const panelWidth = this._panel.offsetWidth

    const spaceBelow = window.innerHeight - triggerRect.bottom
    const top = spaceBelow >= panelHeight + 10
      ? triggerRect.bottom + 8
      : triggerRect.top - panelHeight - 8

    let left = triggerRect.right - panelWidth
    if (left < 10) left = 10
    if (left + panelWidth > window.innerWidth - 10) {
      left = window.innerWidth - panelWidth - 10
    }

    this._panel.style.top = `${Math.max(10, top)}px`
    this._panel.style.left = `${Math.max(10, left)}px`
  }

  _handleOutsideClick(event) {
    if (this._panel && !this._panel.contains(event.target) && !this.element.contains(event.target)) {
      this._removePanel()
    }
  }

  _handleKeydown(event) {
    if (event.key === "Escape") this._removePanel()
  }

  _removePanel() {
    if (this._panel) this._panel.remove()
    this._panel = null
    document.removeEventListener("click", this._outsideClickHandler)
    document.removeEventListener("keydown", this._keydownHandler)
  }
}
